import React, { useEffect, useState } from "react";
import API from "../../Network/API";

/**
 * SharedRecordingPage
 *
 * Public route: /recording/:token
 * No login required. Fetches the short-lived streaming URL from the backend
 * using the opaque share token (UUID) and plays the video inline.
 *
 * If sharing is disabled or the token is invalid, a friendly "unavailable"
 * screen is shown — the raw S3 URL is never exposed to the viewer.
 */
export default function SharedRecordingPage({ token }) {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState("Meeting Recording");
  const [sizeDisplay, setSizeDisplay] = useState(null);
  const [duration, setDuration] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!token) { setState("error"); setErrorMsg("Invalid link."); return; }

    API.get(`/v1/recordings/${token}`)
      .then((res) => {
        if (res.data?.success && res.data.recording_url) {
          setRecordingUrl(res.data.recording_url);
          setMeetingTitle(res.data.meeting_title || "Meeting Recording");
          setSizeDisplay(res.data.size_display || null);
          setDuration(res.data.duration_seconds || null);
          setState("ready");
        } else {
          setState("error");
          setErrorMsg(res.data?.message || "Recording not available.");
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        setState("error");
        setErrorMsg(
          status === 404
            ? "This recording link is no longer active or sharing has been disabled."
            : "Failed to load recording. Please try again later."
        );
      });
  }, [token]);

  const formatDuration = (secs) => {
    if (!secs) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0a",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* Branding header */}
      <div style={{ marginBottom: "28px", textAlign: "center" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "8px 16px", borderRadius: "999px",
          marginBottom: "16px",
        }}>
          <span style={{ color: "#ef4444", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" }}>● REC</span>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>Connects Meeting</span>
        </div>
        <div style={{ color: "#fff", fontSize: "20px", fontWeight: "700", maxWidth: "520px" }}>
          {meetingTitle}
        </div>
        {(sizeDisplay || duration) && (
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "8px" }}>
            {duration && <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>⏱ {formatDuration(duration)}</span>}
            {sizeDisplay && <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>💾 {sizeDisplay}</span>}
          </div>
        )}
      </div>

      {/* Player / States */}
      <div style={{
        width: "100%", maxWidth: "860px",
        borderRadius: "16px", overflow: "hidden",
        background: "#111",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
      }}>
        {state === "loading" && (
          <div style={{
            minHeight: "340px", display: "flex", alignItems: "center",
            justifyContent: "center", flexDirection: "column", gap: "16px",
          }}>
            <div style={{
              width: "44px", height: "44px",
              border: "3px solid rgba(255,255,255,0.1)",
              borderTopColor: "#ef4444", borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px" }}>Loading recording…</span>
          </div>
        )}

        {state === "error" && (
          <div style={{
            minHeight: "320px", display: "flex", alignItems: "center",
            justifyContent: "center", flexDirection: "column", padding: "40px 24px",
          }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔒</div>
            <div style={{ color: "#fff", fontSize: "16px", fontWeight: "600", marginBottom: "8px", textAlign: "center" }}>
              Recording Unavailable
            </div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "14px", textAlign: "center", maxWidth: "360px" }}>
              {errorMsg}
            </div>
            <a
              href="https://connectsapp.com"
              style={{
                marginTop: "24px",
                background: "#ef4444", color: "#fff",
                borderRadius: "8px", padding: "10px 24px",
                textDecoration: "none", fontSize: "13px", fontWeight: "600",
              }}
            >
              Go to Connects
            </a>
          </div>
        )}

        {state === "ready" && recordingUrl && (
          <video
            id="shared-recording-player"
            src={recordingUrl}
            controls
            autoPlay={false}
            playsInline
            style={{ width: "100%", display: "block", background: "#000", outline: "none" }}
            onError={() => {
              setState("error");
              setErrorMsg("Failed to play recording. The link may have expired — please ask the host to re-share.");
            }}
          >
            Your browser does not support video playback.
          </video>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: "20px", color: "rgba(255,255,255,0.2)", fontSize: "11px", textAlign: "center" }}>
        Shared via{" "}
        <a href="https://connectsapp.com" style={{ color: "rgba(255,255,255,0.35)", textDecoration: "none" }}>
          Connects
        </a>
        {" · "}This link was shared by the host of the meeting.
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
